import { BodyGesture } from './BodyTracker';

export class CameraOverlay {
  private container: HTMLElement;
  private video: HTMLVideoElement;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private visible = false;
  private skeletonData: any[] | null = null;
  private currentAction = '';
  private actionTimer = 0;

  constructor() {
    this.container = document.getElementById('camera-overlay')!;
    this.video = document.getElementById('cam-video') as HTMLVideoElement;
    this.canvas = document.getElementById('cam-canvas') as HTMLCanvasElement;
    this.ctx = this.canvas.getContext('2d')!;
  }

  show() {
    this.visible = true;
    this.container.style.display = 'block';
    this.position();
  }

  hide() {
    this.visible = false;
    this.container.style.display = 'none';
  }

  setVideoStream(stream: MediaStream) {
    this.video.srcObject = stream;
    this.video.play();
  }

  setVideoElement(video: HTMLVideoElement) {
    // If already has its own stream, use that
    if (video.srcObject) {
      this.video.srcObject = video.srcObject;
      this.video.play();
    }
  }

  updateSkeleton(landmarks: any[]) {
    this.skeletonData = landmarks;
  }

  showAction(action: string) {
    this.currentAction = action;
    this.actionTimer = performance.now();
  }

  private position() {
    const gameRect = document.getElementById('game-container')?.getBoundingClientRect();
    if (!gameRect) return;
    const size = Math.min(240, gameRect.width * 0.18);
    this.container.style.width = size + 'px';
    this.container.style.height = (size * 0.75) + 'px';
    this.container.style.top = '10px';
    this.container.style.right = '10px';
    this.container.style.left = 'auto';
    this.canvas.width = 320;
    this.canvas.height = 240;
  }

  drawFrame() {
    if (!this.visible) return;

    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    // Draw video frame
    if (this.video.readyState >= 2) {
      this.ctx.save();
      this.ctx.scale(-1, 1);
      this.ctx.drawImage(this.video, -320, 0, 320, 240);
      this.ctx.restore();
    }

    // Draw skeleton
    if (this.skeletonData) {
      this.drawSkeleton();
    }

    // Draw action label
    if (this.currentAction && performance.now() - this.actionTimer < 1500) {
      this.ctx.save();
      this.ctx.shadowColor = '#00f0ff';
      this.ctx.shadowBlur = 15;
      this.ctx.fillStyle = '#00f0ff';
      this.ctx.font = 'bold 18px Arial';
      this.ctx.textAlign = 'center';
      this.ctx.fillText(this.currentAction, 160, 30);
      this.ctx.restore();
    }

    // Border glow animation
    const pulse = 0.3 + Math.sin(performance.now() * 0.005) * 0.2;
    this.container.style.boxShadow = `0 0 ${15 + pulse * 10}px rgba(0,240,255,${pulse})`;

    requestAnimationFrame(() => this.drawFrame());
  }

  private drawSkeleton() {
    const lm = this.skeletonData;
    const connections = [
      [11,12],[11,23],[12,24],[23,24],
      [11,13],[13,15],[12,14],[14,16],
      [15,17],[17,19],[15,19],[15,21],[16,18],[18,20],[16,20],[16,22],
      [23,25],[25,27],[27,29],[27,31],[29,31],
      [24,26],[26,28],[28,30],[28,32],[30,32],
    ];

    const toScreen = (idx: number) => {
      const p = lm[idx];
      return { x: (1 - p.x) * this.canvas.width, y: p.y * this.canvas.height };
    };

    this.ctx.strokeStyle = '#00f0ff';
    this.ctx.lineWidth = 2;
    this.ctx.shadowColor = '#00f0ff';
    this.ctx.shadowBlur = 8;

    for (const [a, b] of connections) {
      if (lm[a] && lm[b]) {
        const p1 = toScreen(a);
        const p2 = toScreen(b);
        this.ctx.beginPath();
        this.ctx.moveTo(p1.x, p1.y);
        this.ctx.lineTo(p2.x, p2.y);
        this.ctx.stroke();
      }
    }

    // Joint circles
    lm.forEach((_p: any, i: number) => {
      if (i >= 33) return;
      const pos = toScreen(i);
      this.ctx.fillStyle = '#00f0ff';
      this.ctx.shadowBlur = 10;
      this.ctx.beginPath();
      this.ctx.arc(pos.x, pos.y, 3, 0, Math.PI * 2);
      this.ctx.fill();
    });

    this.ctx.shadowBlur = 0;
  }

  isVisible(): boolean { return this.visible; }
}
